/**
 * Phase 248 — Form hydrators (React controlled-input safety net).
 *
 * Why this file exists
 * --------------------
 * MongoDB returns `null` for fields that were never set on a document. When
 * those raw docs land directly into an Edit modal's form state — e.g.
 * `setEditEmployee(emp)` — React boots every `<Input value={null} />` as an
 * **uncontrolled** input and flips it to **controlled** on the first
 * keystroke. The browser console then fires:
 *
 *     "Warning: A component is changing an uncontrolled input to be
 *      controlled. This is likely caused by the value changing from
 *      undefined to a defined value, which should not happen…"
 *
 * The fix is mechanical (coerce every form-bound field to `''` on hydrate)
 * but it was previously duplicated inline in Students.js (`_editFormShape`)
 * and partially inlined in HonorsManager.jsx, AdminInvoices.js, etc. This
 * module centralizes the pattern so every Edit modal in the app uses the
 * SAME guarantees:
 *
 *   1. Strings that were `null`/`undefined` become `''`.
 *   2. Numeric fields keep their numeric type unless missing → `''`.
 *   3. Arrays (multi-selects) keep their array shape unless missing → `[]`.
 *   4. Passwords are NEVER pre-filled when editing an existing record.
 *
 * Public API
 * ----------
 * - `blank(v)`                — coerce null/undefined to ''
 * - `blankArray(v)`           — coerce null/undefined to []
 * - `hydrateForEdit(record, fieldMap)` — generic shape-driven hydrator
 * - `hydrateStudentForm(s)`   — Students.js Edit modal (Phase 247.c parity)
 * - `hydrateEmployeeForm(e)`  — HRMS.js Edit modal
 * - `hydrateTournamentForm(t)` & `hydratePlacementForm(p)` — Honors admin
 *
 * Usage
 * -----
 *   import { hydrateEmployeeForm } from '@/utils/formHydrators';
 *   onClick={() => setEditEmployee(hydrateEmployeeForm(emp))}
 */

/** Coerce a single primitive: null/undefined → ''. Pass-through otherwise. */
export const blank = (v) => (v === null || v === undefined ? '' : v);

/** Coerce array-shaped field: null/undefined → []. Pass-through otherwise. */
export const blankArray = (v) => (Array.isArray(v) ? v : []);

/** Coerce numeric field: null/undefined → ''. 0 stays 0 (NOT blanked) so
 *  legitimate zero values render correctly. */
export const blankNumeric = (v) => (v === null || v === undefined ? '' : v);

/**
 * Generic shape-driven hydrator.
 *
 * `fieldMap` is a `{ field: 'string'|'number'|'array' }` declaration so the
 * hydrator never has to guess. Unknown fields fall through unchanged so the
 * caller's spread (`...record`) keeps shape parity (e.g. nested objects like
 * `financial_profile` survive intact for downstream PUT calls).
 */
export function hydrateForEdit(record, fieldMap) {
  if (!record) return record;
  const out = { ...record };
  for (const [field, kind] of Object.entries(fieldMap)) {
    const raw = record[field];
    if (kind === 'string') out[field] = blank(raw);
    else if (kind === 'number') out[field] = blankNumeric(raw);
    else if (kind === 'array') out[field] = blankArray(raw);
    else out[field] = blank(raw); // safe default
  }
  return out;
}

// =====================================================================
// Domain-specific hydrators — one per Edit modal that needs it.
// =====================================================================

/**
 * Students.js — Edit modal hydrator. Mirrors the legacy `_editFormShape`
 * helper from Phase 247.c (preserved verbatim so behavior stays identical).
 */
export function hydrateStudentForm(s) {
  if (!s) return s;
  return hydrateForEdit(
    { ...s, password: '' }, // never pre-fill saved passwords
    {
      name: 'string',
      email: 'string',
      phone: 'string',
      dob: 'string',
      course_id: 'string',
      batch_id: 'string',
      guardian_name: 'string',
      guardian_phone: 'string',
      blood_group: 'string',
      height: 'number',
      weight: 'number',
      address: 'string',
      emergency_contact: 'string',
      // Phase 245 — financial profile fields persisted on the student doc
      base_fee: 'number',
      scholarship_amount: 'number',
      scholarship_notes: 'string',
    },
  );
}

/**
 * HRMS.js — Employee Edit hydrator. Closes the controlled/uncontrolled gap
 * for the 10 raw fields the EmployeeForm component renders directly from a
 * Mongo doc (designation, base_salary, join_date, pan, aadhar, etc.).
 */
export function hydrateEmployeeForm(e) {
  if (!e) return e;
  return hydrateForEdit(
    { ...e, password: '' }, // never pre-fill saved passwords on edit
    {
      name: 'string',
      email: 'string',
      phone: 'string',
      role: 'string',
      department_id: 'string',
      designation: 'string',
      base_salary: 'number',
      join_date: 'string',
      pan: 'string',
      aadhar: 'string',
      bank_account: 'string',
      bank_ifsc: 'string',
      assigned_center_ids: 'array',
      assigned_batch_ids: 'array',
    },
  );
}

/**
 * HonorsManager.jsx — Tournament Edit hydrator. Backs the
 * "Editing #X" form in the Honors admin page.
 *
 * NOTE: HonorsManager's edit form only round-trips a fixed subset of fields
 * back to the PUT endpoint (NOT the full Mongo doc — extras like `id`,
 * `academy_id`, `created_at` are excluded so the API doesn't reject them
 * as unknown). So this hydrator returns ONLY the picked fields, identical
 * to the legacy editTournament behavior, with controlled-input safety on
 * top.
 */
export function hydrateTournamentForm(t) {
  if (!t) return t;
  return {
    tournament_name: blank(t.tournament_name),
    year: blankNumeric(t.year),
    rank: blank(t.rank),
    division: blank(t.division),
    summary: blank(t.summary),
  };
}

/**
 * HonorsManager.jsx — Placement Edit hydrator. Same picking strategy as
 * hydrateTournamentForm — only the form-bound subset, not the full doc.
 */
export function hydratePlacementForm(p) {
  if (!p) return p;
  return {
    athlete_name: blank(p.athlete_name),
    destination_club: blank(p.destination_club),
    signing_date: blank(p.signing_date),
    notes: blank(p.notes),
    student_id: blank(p.student_id),
  };
}
